import { useState } from 'react';
import {
  useAgentKnowledgeBases,
  useKnowledgeBases,
  useLinkKnowledgeBase,
  useUnlinkKnowledgeBase,
} from '@/lib/api/knowledge';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Input';

interface AgentKnowledgePanelProps {
  agentId: string;
}

/** Linked-knowledge-bases selector for an agent: list, link, unlink (spec 0014, AC-13). */
export function AgentKnowledgePanel({ agentId }: AgentKnowledgePanelProps) {
  const linked = useAgentKnowledgeBases(agentId);
  const allBases = useKnowledgeBases();
  const link = useLinkKnowledgeBase(agentId);
  const unlink = useUnlinkKnowledgeBase(agentId);
  const [selected, setSelected] = useState('');

  const linkedIds = new Set((linked.data ?? []).map((b) => b.id));
  const available = (allBases.data ?? []).filter((b) => !linkedIds.has(b.id));

  function handleLink() {
    if (!selected) return;
    link.mutate(selected, { onSuccess: () => setSelected('') });
  }

  return (
    <Card
      title="Knowledge"
      description="Knowledge bases this agent can search to ground its answers."
    >
      {linked.isLoading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (linked.data ?? []).length === 0 ? (
        <p className="text-sm text-gray-500">No knowledge bases linked yet.</p>
      ) : (
        <ul className="mb-4 divide-y divide-gray-100">
          {linked.data?.map((base) => (
            <li
              key={base.id}
              className="flex items-center justify-between gap-3 py-2"
              data-testid="agent-linked-knowledge-base"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-gray-900">{base.name}</p>
                {base.description && (
                  <p className="truncate text-xs text-gray-500">{base.description}</p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => unlink.mutate(base.id)}
                disabled={unlink.isPending}
                data-testid="unlink-knowledge-base-button"
              >
                Unlink
              </Button>
            </li>
          ))}
        </ul>
      )}

      {(linked.data ?? []).length > 0 && (
        <p className="mb-4 text-xs text-gray-500" data-testid="knowledge-tool-note">
          The knowledge search tool is active for this agent.
        </p>
      )}

      {(link.error || unlink.error) && (
        <p
          role="alert"
          className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {((link.error ?? unlink.error) as Error).message}
        </p>
      )}

      {available.length > 0 && (
        <div className="flex items-end gap-2">
          <Select
            label="Link a knowledge base"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            data-testid="link-knowledge-base-select"
          >
            <option value="">Select a knowledge base...</option>
            {available.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            variant="secondary"
            onClick={handleLink}
            disabled={!selected || link.isPending}
            data-testid="link-knowledge-base-button"
          >
            Link
          </Button>
        </div>
      )}
    </Card>
  );
}
